import * as React from "react";
import { Checkbox, Switch, Radio, FormInstance, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ProjectTabProps } from "../Projects";
import { API_PROJECT_FUNCTIONS, API_MODULEDATA_MODULE, API_ADMINPROJECT_LOGINMODE } from "General/APIEnums";
import { Loader } from "Components/General/Loader";
import { ReportsFunctions } from "./ReportsFunctions";

interface FunctionConfig {
  func: API_PROJECT_FUNCTIONS;
  description: string;
}

interface FunctionsSelectionProps extends WithTranslation {
  functionsActive: { [key in API_PROJECT_FUNCTIONS]: boolean };
  /**
   * Use 'classicAndModern' by default, if type doesn't exist
   */
  type?: "classicAndModern" | "classic" | "modern";
  /**
   * Use 'GENERAl' by default, if module doesn't exist
   * GENERAL - pseudo "module" that includes functions that are independent of the specific module
   */
  module?: API_MODULEDATA_MODULE | "GENERAL";
}

const FunctionsSelection = withTranslation(I18N_NS.ADMIN)(
  class extends React.Component<FunctionsSelectionProps> {
    // tslint:disable-next-line: max-line-length
    static defaultProps = {
      module: "GENERAL" as const,
      type: "classicAndModern" as "classicAndModern" | "classic" | "modern",
    };

    private functionsConfig: {
      classicAndModern: { [key: string]: FunctionConfig[] };
      classic?: { [key: string]: FunctionConfig[] };
      modern?: { [key: string]: FunctionConfig[] };
    } = {
      classicAndModern: {
        DOCUMENTS: [
          {
            func: API_PROJECT_FUNCTIONS.METADATA,
            description: "Enable meta data",
          },
          {
            func: API_PROJECT_FUNCTIONS.FILECHECK,
            description: "Interface File Check Mobile",
          },
          {
            func: API_PROJECT_FUNCTIONS.FILEPREVIEW,
            description: "File preview",
          },
          {
            func: API_PROJECT_FUNCTIONS.FILENAMECHECK,
            description: "Filename check according to plan key",
          },
          {
            func: API_PROJECT_FUNCTIONS.AUTOMATICDIRMAPPING,
            description: "Automatic directory mapping (meta data and plan key necessary)",
          },
          {
            func: API_PROJECT_FUNCTIONS.ONLINEVIEWER,
            description: "Enable Online Viewer (Rx360)",
          },
          {
            func: API_PROJECT_FUNCTIONS.EASYVIEWER,
            description: "Enable Easy Viewer",
          },
          {
            func: API_PROJECT_FUNCTIONS.WEBVIEWER,
            description: "Enable WebViewer (Apryse)",
          },
          {
            func: API_PROJECT_FUNCTIONS.PDFCONVERTATION,
            description: "Enable PDF conversion for doc(x), xls(x), ppt(x), dwg, dxf",
          },
          // {
          //   func: API_PROJECT_FUNCTIONS.PLANCHECK2GO,
          //   description: 'Inserting a Plancheck2Go-QR code on PDF documents (when printing)'
          // },
          {
            func: API_PROJECT_FUNCTIONS.FULLTEXTSEARCH,
            description: "Full text search",
          },
          {
            func: API_PROJECT_FUNCTIONS.WEBDAV,
            description: "WebDAV in Documents module",
          },
          {
            func: API_PROJECT_FUNCTIONS.WEBDAV_FOR_VIEWS,
            description: "WebDAV in Views module",
          },
          {
            func: API_PROJECT_FUNCTIONS.WEBDAV_FOR_WORKFLOWS,
            description: "WebDAV in Workflow module",
          },
          {
            func: API_PROJECT_FUNCTIONS.SYNCHRONIZATION_BOX,
            description: "Synchronization with BOX.com",
          },

          {
            func: API_PROJECT_FUNCTIONS.SYNCHRONIZATION_PROBIS,
            description: "Synchronization with PROBIS",
          },
          {
            func: API_PROJECT_FUNCTIONS.SYNCHRONIZATION_BIM_COLLAB,
            description: "Synchronization with BIMcollab",
          },
          {
            func: API_PROJECT_FUNCTIONS.AI_CHAT,
            description: "Activate AI in Webviewer",
          },
        ],
      },
      classic: {
        GENERAL: [
          {
            func: API_PROJECT_FUNCTIONS.PLANVIEW,
            description: "Plan View (metadata necessary)",
          },
        ],
      },
      modern: {
        DOCUMENTS: [
          {
            func: API_PROJECT_FUNCTIONS.OFFICE_VIEWER,
            description: "Office Online",
          },
          {
            func: API_PROJECT_FUNCTIONS.BCF_VIEWER,
            description: "BCF Viewer",
          },
          {
            func: API_PROJECT_FUNCTIONS.FILE_DISTRIBUTION_MATRIX,
            description: "File Distribution Matrix (metadata necessary)",
          },
          {
            func: API_PROJECT_FUNCTIONS.FILE_LABELS,
            description: "File labels",
          },
          {
            func: API_PROJECT_FUNCTIONS.BIM_VIEWER,
            description: "BIM Viewer",
          },
        ],
        MAIL: [
          {
            func: API_PROJECT_FUNCTIONS.PROJECT_INBOXES,
            description: "Intelligent project inbox",
          },
        ],
      },
    };

    renderCheckbox = (items: FunctionConfig[]) =>
      items.map((item, idx) => {
        const { t, functionsActive } = this.props;

        return (
          <div key={idx}>
            <Form.Item
              name={["functions", item.func]}
              initialValue={functionsActive && functionsActive[item.func]}
              valuePropName="checked"
              className="ant-form-item-with-no-height"
            >
              <Checkbox>{t(item.description)}</Checkbox>
            </Form.Item>
          </div>
        );
      });

    render() {
      const { type, module } = this.props;
      return this.renderCheckbox(this.functionsConfig[type][module]);
    }
  }
);

interface FunctionsTabProps extends ProjectTabProps, WithTranslation {
  modulesActive: { [key in API_MODULEDATA_MODULE]: boolean };
  functionsActive: { [key in API_PROJECT_FUNCTIONS]: boolean };
  functionsOptions: API_FunctionOptions;
  loginmode: string;
  hasFunctions: boolean;
  groupsForRights: API_Group[];
  formRef: React.RefObject<FormInstance>;
}

export const FunctionsTab = withTranslation(I18N_NS.ADMIN)(
  observer(
    class extends React.Component<FunctionsTabProps, { isStandart: boolean }> {
      constructor(props: FunctionsTabProps) {
        super(props);
        this.state = {
          isStandart: props.loginmode === "STANDARD",
        };
      }

      handleSwitcherChange = () => {
        const type = API_ADMINPROJECT_LOGINMODE;

        this.setState({ isStandart: !this.state.isStandart }, () => {
          this.state.isStandart
            ? this.props.formRef.current.setFieldsValue({ loginMode: type.STANDARD })
            : this.props.formRef.current.setFieldsValue({ loginMode: type.MFA_FOR_ALL_USERS_FORBID_GUESTS });
        });
      };

      render2FASwitcher = () => {
        const { t, loginmode } = this.props;
        const type = API_ADMINPROJECT_LOGINMODE;
        return (
          <div className="mb-2">
            <div className="mb-2">
              <Switch
                onChange={this.handleSwitcherChange}
                defaultChecked={loginmode !== type.STANDARD}
                style={{ marginRight: `5px` }}
              />
              {t("Enable 2-Factor-Authentication (2FA)")}
            </div>
            <Form.Item name={"loginMode"} initialValue={loginmode !== type.STANDARD ? loginmode : type.STANDARD}>
              <Radio.Group
                disabled={loginmode === type.STANDARD && this.state.isStandart}
                style={{ marginLeft: "25px" }}
              >
                <Radio className="block" value={type.MFA_FOR_ALL_USERS_FORBID_GUESTS}>
                  {t("For all users, no guest access possible")}
                </Radio>
                <Radio className="block" value={type.MFA_FOR_ALL_USERS_ALLOW_GUESTS}>
                  {t("For all users, but allow guest access")}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        );
      };

      render() {
        if (!this.props.hasFunctions) {
          return <Loader />;
        }

        const { t, modulesActive, functionsActive, formRef } = this.props;

        return (
          <div>
            <h2>{t("Classic and Modern")}</h2>
            {this.render2FASwitcher()}

            {Object.prototype.hasOwnProperty.call(modulesActive, "DOCUMENTS") && modulesActive.DOCUMENTS && (
              <div className="mb-lg">
                <h3>{t("Documents")}</h3>
                <FunctionsSelection functionsActive={functionsActive} module={API_MODULEDATA_MODULE.DOCUMENTS} />
              </div>
            )}

            {/* <h2>{t('Purely Classic')}</h2>
        <div className="mb-lg">
          <FunctionsSelection
            functionsActive={functionsActive}
            type={'classic'}
            form={form}
          />
        </div> */}

            <h2>{t("Purely Modern")}</h2>
            <FunctionsSelection
              functionsActive={functionsActive}
              module={API_MODULEDATA_MODULE.DOCUMENTS}
              type={"modern"}
            />
            <FunctionsSelection functionsActive={functionsActive} module={API_MODULEDATA_MODULE.MAIL} type={"modern"} />
            <ReportsFunctions
              formRef={formRef}
              groupsForRights={this.props.groupsForRights}
              reportsModule={this.props.functionsActive.REPORTS}
            />
          </div>
        );
      }
    }
  )
);
